// src/components/riskassessment/StrategyRecommendations.js
//
// Shows the full ARCrypto vault catalog (7 vaults) grouped by strategy
// RISK CATEGORY: Conservative / Moderate / Aggressive.
//
// 2026-07-06 change (student feedback + compliance): removed the prescribed
// per-vault percentage allocations. We now categorize each managed vault by
// its risk character instead of prescribing an exact allocation — this is
// simpler for members and avoids prescribing specific position sizes.
//
// 2026-07-08 change (compliance): removed the static backtest return figures
// (e.g. "+9,484% backtest") from each card. We no longer publish performance
// numbers inside the app — instead each vault links to its live Enzyme page,
// where real-time results come straight from the source. Max drawdown stays,
// as it's a downside/risk disclosure, not a gains claim.
//
// The old ALLOCATION_BY_PROFILE table and the local scoreToProfile() were
// deleted — they duplicated (and had drifted from) the scoring engine.
// The engine (cryptoCalculationUtils.js) is now the single source of truth
// for profiles; this component just reads profileType for the header label.
//
// Source of truth for vault stats: ARCrypto_Risk_Assessment_Blueprint.md
//
// Props:
//   riskScore       — composite 0–100 score (shown in the header)
//   profileType     — "Conservative" | "Moderate" | "Aggressive"
//   safetyOverrides — array of { rule, message } from the scoring engine
//   className

import React from "react";
import { Shield, Target, AlertTriangle, ExternalLink } from "lucide-react";
import { normalizeProfileType } from "./utils/cryptoCalculationUtils";

/* ─── Vault catalog — from blueprint section 1 ────────────────────────── */
// `category` = the vault's risk character, derived from its risk score,
// leverage, and historical max drawdown. Aligns with the app's risk-badge
// bands (≤4 conservative, 5–6 moderate, 7+ aggressive).
const VAULTS = [
  {
    key: "leoBTC",
    name: "Leo BTC Bull Run",
    category: "Conservative",
    risk: 3,
    type: "Long only",
    asset: "BTC",
    leverage: "None",
    maxDD: -7.43,
    addr: "0xb99463e453e2a7ce9fcea6df0bf6991cffef13d1",
    behavior: "Sits in cash, only deploys on confirmed trends",
    color: "#22c55e",
  },
  {
    key: "leoETH",
    name: "Leo ETH",
    category: "Conservative",
    risk: 4,
    type: "Long only",
    asset: "ETH",
    leverage: "None",
    maxDD: -20.69,
    addr: "0xc0ba7e66631106f5b8e5716b50bd167431b7f6ca",
    behavior: "ETH momentum, 100% capital on trend",
    color: "#10b981",
  },
  {
    key: "gemBTC",
    name: "Gemini BTC",
    category: "Moderate",
    risk: 5,
    type: "Long/short",
    asset: "BTC",
    leverage: "None",
    maxDD: -15.79,
    addr: "0xd065f37a0ea7f277bf36d93043d20bfb58b93761",
    behavior: "Frequent trading with controlled losses",
    color: "#3b82f6",
  },
  {
    key: "gemini",
    name: "Gemini",
    category: "Moderate",
    risk: 5,
    type: "Long/short",
    asset: "BTC/ETH/SOL",
    leverage: "Derivatives",
    maxDD: -17.6,
    addr: "0x11d41452fcd89c3622f20e680ebbec8587483a63",
    behavior: "33/33/33 split, diversified across three assets",
    color: "#6366f1",
  },
  {
    key: "scorpioV",
    name: "Scorpio V",
    category: "Aggressive",
    risk: 7,
    type: "Long/short",
    asset: "BTC",
    leverage: "Up to 12x",
    maxDD: -20.44,
    addr: "0xd2c89dc816d0d2e9a52ab5124c66a2a731a74a7d",
    behavior: "High-conviction leveraged BTC trends",
    color: "#f59e0b",
  },
  {
    key: "gemETH",
    name: "Gemini ETH",
    category: "Aggressive",
    risk: 8,
    type: "Long/short",
    asset: "ETH",
    leverage: "None",
    maxDD: -37.5,
    addr: "0xfde46c2e43d54bbb94eb21452aac711f9b9d8e0e",
    behavior: "Highest returns + deepest drawdowns in the suite",
    color: "#ef4444",
  },
  {
    key: "gemSOL",
    name: "Gemini SOL",
    category: "Aggressive",
    risk: 8,
    type: "Long/short directional",
    asset: "SOL",
    leverage: "None",
    maxDD: -49,
    addr: "0xc64197bf72d0ead4bd3563c4dab23b849848268c",
    behavior: "Directional SOL — struggles in chop, shines in trends",
    color: "#dc2626",
  },
];

/* ─── Category presentation ───────────────────────────────────────────── */
// Semantic scale colors for the category badge (conservative→aggressive is
// a meaningful scale, so it keeps green→amber→red). The CYAN accent + glow
// is the Risk tab's signature and is applied to the section chrome.
const CATEGORY_ORDER = ["Conservative", "Moderate", "Aggressive"];
const CATEGORY_META = {
  Conservative: {
    dot: "#22c55e",
    badge: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    blurb: "Long-only, no leverage, shallow drawdowns — built for capital preservation.",
  },
  Moderate: {
    dot: "#eab308",
    badge: "text-arc-gold bg-arc-gold/10 border-arc-gold/30",
    blurb: "Long/short with controlled losses — balanced growth without meaningful leverage.",
  },
  Aggressive: {
    dot: "#ef4444",
    badge: "text-red-300 bg-red-500/10 border-red-500/30",
    blurb: "Leverage and/or deeper drawdowns — highest return potential, highest volatility.",
  },
};

// Risk tab signature accent (NEON cyan) + soft glow for the section chrome.
const ARC_CYAN = "#22d3ee";
const cyanGlow = { boxShadow: `0 0 18px rgba(34, 211, 238, 0.18)` };

// All ARCrypto vaults live on Enzyme (Arbitrum). The live vault page is the
// single source of truth for real-time performance — we link out rather than
// republish (and risk staling) performance figures inside the app.
const enzymeVaultUrl = (addr) =>
  `https://app.enzyme.finance/vault/${addr}?network=arbitrum`;

const riskBadgeColor = (risk) => {
  if (risk <= 4) return "text-arc-light bg-emerald-500/10 border-emerald-500/30";
  if (risk <= 6) return "text-arc-gold bg-arc-gold/10 border-arc-gold/30";
  return "text-arc-light bg-red-500/10 border-red-500/30";
};

const VaultCard = ({ v }) => (
  <div
    className="rounded-2xl border border-arc-line bg-white/[0.02] p-5"
    style={{ borderLeft: `3px solid ${v.color}` }}
  >
    <div className="flex items-start justify-between mb-2">
      <h4 className="font-semibold text-arc-ink text-lg">{v.name}</h4>
      <span
        className={`text-[10px] font-mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border ${riskBadgeColor(
          v.risk
        )}`}
      >
        Risk {v.risk}/10
      </span>
    </div>

    {/* Category label replaces the old "% of portfolio" line */}
    <div className="mb-3">
      <span
        className={`text-[11px] font-mono uppercase tracking-[0.14em] px-2.5 py-1 rounded-full border ${CATEGORY_META[v.category].badge}`}
      >
        {v.category} Strategy
      </span>
    </div>

    <div className="space-y-1 text-xs text-arc-muted">
      <div className="flex justify-between">
        <span>Asset</span>
        <span className="text-arc-ink num">{v.asset}</span>
      </div>
      <div className="flex justify-between">
        <span>Type</span>
        <span className="text-arc-ink">{v.type}</span>
      </div>
      <div className="flex justify-between">
        <span>Leverage</span>
        <span className="text-arc-ink num">{v.leverage}</span>
      </div>
      <div className="flex justify-between">
        <span>Max DD</span>
        <span className="num text-red-400">{v.maxDD}%</span>
      </div>
    </div>

    <p className="text-xs text-arc-dim italic mt-3 leading-relaxed">{v.behavior}</p>

    {/* Live results — links to the vault's actual Enzyme page (real-time, from
        the source) instead of showing a static/backtest performance figure. */}
    <a
      href={enzymeVaultUrl(v.addr)}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-4 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.14em] transition-opacity hover:opacity-80"
      style={{ color: ARC_CYAN }}
    >
      View live results
      <ExternalLink size={13} />
    </a>
  </div>
);

const StrategyRecommendations = ({
  riskScore = 0,
  profileType,
  safetyOverrides = [],
  className = "",
}) => {
  const profile = normalizeProfileType(profileType || "Moderate");

  return (
    <div className={`surface overflow-hidden ${className}`}>
      {/* Header */}
      <div className="mesh p-6 border-b border-arc-line">
        <div className="flex items-center gap-3 mb-2">
          <Target className="w-5 h-5" strokeWidth={1.75} style={{ color: ARC_CYAN }} />
          <p className="eyebrow">Profile · {profile}</p>
        </div>
        <h3 className="headline text-2xl">Managed Vaults by Strategy Risk</h3>
        <p className="text-arc-muted text-sm mt-2 max-w-2xl">
          The full ARCrypto vault suite, grouped by risk character —{" "}
          <span className="text-arc-ink font-semibold">Conservative</span>,{" "}
          <span className="text-arc-ink font-semibold">Moderate</span>, and{" "}
          <span className="text-arc-ink font-semibold">Aggressive</span>. Your composite score
          of {Math.round(riskScore)}/100 places you in the{" "}
          <span className="text-arc-ink font-semibold">{profile}</span> profile — use it as a
          lens for which strategy character fits your temperament. Illustrative and educational;
          not personalized advice or a recommendation to buy any specific vault.
        </p>
      </div>

      {/* Safety override notice */}
      {safetyOverrides && safetyOverrides.length > 0 && (
        <div className="mx-6 mt-6 rounded-2xl border border-arc-deep/40 bg-arc-deep/10 p-4">
          <div className="flex items-start gap-2.5">
            <AlertTriangle size={16} className="text-arc-light flex-shrink-0 mt-0.5" />
            <div>
              <p className="eyebrow mb-1.5 text-arc-light">Capacity considerations</p>
              <p className="text-sm text-arc-muted">
                Educationally, when answers reflect patterns like these, illustrative examples
                tend to lean toward lower-volatility mixes. Factors worth understanding:
              </p>
              <ul className="mt-2 space-y-1 text-sm text-arc-muted">
                {safetyOverrides.map((o, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-arc-light">•</span>
                    <span>{o.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Vaults grouped by category */}
      <div className="p-6 space-y-8">
        {CATEGORY_ORDER.map((cat) => {
          const inCat = VAULTS.filter((v) => v.category === cat);
          if (inCat.length === 0) return null;
          const meta = CATEGORY_META[cat];
          const isUserBucket = cat === profile;
          return (
            <div key={cat}>
              {/* Section header — cyan signature accent + glow */}
              <div
                className="flex items-center gap-3 mb-4 pb-2 border-b"
                style={{ borderColor: "rgba(34,211,238,0.25)" }}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ background: meta.dot, ...cyanGlow }}
                />
                <h4 className="headline text-lg" style={{ color: ARC_CYAN }}>
                  {cat} Strategies
                </h4>
                {isUserBucket && (
                  <span
                    className="text-[10px] font-mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border"
                    style={{ color: ARC_CYAN, borderColor: "rgba(34,211,238,0.4)", ...cyanGlow }}
                  >
                    Your profile
                  </span>
                )}
                <span className="text-xs text-arc-dim ml-auto hidden sm:block">{meta.blurb}</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {inCat.map((v) => (
                  <VaultCard key={v.key} v={v} />
                ))}
              </div>
            </div>
          );
        })}

        {/* General cash note */}
        <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-5">
          <div className="flex items-center gap-2 mb-2">
            <Shield size={14} className="text-arc-dim" />
            <p className="eyebrow">On cash</p>
          </div>
          <p className="text-xs text-arc-muted leading-relaxed">
            A cash / stables buffer provides psychological safety and rebalancing optionality.
            Even higher-risk temperaments typically keep some capital liquid.
          </p>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="px-6 pb-6">
        <a
          href="https://app.enzyme.finance"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-tactile btn-outline w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm"
        >
          View vaults on Enzyme.finance
          <ExternalLink size={14} />
        </a>
      </div>
    </div>
  );
};

export default StrategyRecommendations;
